import { usePostHog } from "posthog-js/react";
import { BenefitCard } from "./components/BenefitCard";
import { RenterBenefit } from "providers/auth/types";
import { useAuth } from "../../providers/auth";
import { useQuery } from "@tanstack/react-query";
import api from "api";
import { RentReportingCard } from "./components/RentReportingCard";
import { BenefitLayout } from "providers/benefit-layout";
import { IssueCard } from "./components/IssueCard";
import { PostPageView } from "helpers/postEvent";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, ButtonVariant } from "shared/components/Button";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import { useMemo } from "react";
import { Modal } from "shared/components/Modal";
import { useURLQuery } from "helpers/useURLQuery";
import { CustomerIssue } from "types/issue";
import {
  getPendingEnrollmentReason,
  getPendingReason
} from "helpers/getPendingReason";
import { Redirect } from "common/Redirect";

export const HubView = () => {
  const { customer, partner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useURLQuery();

  const { featureFlags } = usePostHog();
  const isNoHub = Boolean(featureFlags?.isFeatureEnabled("hide_hub_page"));

  // locations state has fromRentReportingSuccess
  const fromRentReportingSuccess =
    location.state?.fromRentReportingSuccess ||
    Boolean(query.get("fromRentReportingSuccess"));

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ["LATEST_ENROLLMENT"],
    refetchInterval: 5000,
    queryFn: () =>
      api.getEnrollmentCases().then((res) => {
        return res.data.items?.at(-1);
      }),
    enabled: Boolean(
      partner?.renter_benefits?.includes(RenterBenefit.RENT_REPORTING)
    )
  });
  const issuesMeta = useQuery({
    queryKey: ["ISSUES"],
    queryFn: () =>
      api.getDashboard().then(({ data }) => {
        return data.customer_issues
          .concat(
            data.enrollments.reduce(
              (acc: CustomerIssue[], e) =>
                e.id === enrollment?.id ? acc.concat(e?.issues || []) : acc,
              []
            )
          )
          .sort((a, b) => {
            if (a.slug === "bank-link-bypassed") return -1;
            if (b.slug === "bank-link-bypassed") return 1;
            return 0;
          });
      }),
    refetchInterval: 5000,
    enabled: Boolean(partner?.renter_benefits?.length)
  });

  const pendingBenefits: RenterBenefit[] = [];
  if (partner?.renter_benefits?.length)
    partner.renter_benefits.forEach((benefit) => {
      if (benefit !== RenterBenefit.RENT_REPORTING)
        pendingBenefits.push(benefit);

      if (benefit === RenterBenefit.RENT_REPORTING && !enrollment?.submitted)
        pendingBenefits.push(benefit);
    });

  const theme = useMemo(() => {
    const ret: Record<string, string> = {};
    if (partner?.colors?.primary && Object.keys(partner.colors.primary).length)
      Object.entries(partner.colors.primary).forEach(([key, value]) => {
        ret[`--primary-${key}`] = value;
      });

    if (partner?.base_border_radius)
      ret["--base-border-radius"] = partner.base_border_radius;

    if (partner?.colors?.background)
      Object.entries(partner.colors.background).forEach(([key, value]) => {
        ret[`--background-${key}`] = value;
      });

    if (partner?.font_family) {
      if (partner.font_family_link) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = partner?.font_family_link;
        if (partner.font_family_link.includes("fonts.googleapis.com")) {
          const preconnect = document.createElement("link");
          preconnect.rel = "preconnect";
          preconnect.href = "https://fonts.gstatic.com";
          document.head.appendChild(preconnect);
          const preconnect2 = document.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://fonts.googleapis.com";
          preconnect2.crossOrigin = "anonymous";
          document.head.appendChild(preconnect2);
        }
        document.head.appendChild(link);
      }

      ret["--font-family"] = partner.font_family;
    }
    return ret;
  }, [partner]);

  if ((partner?.renter_benefits?.length || 0) === 0)
    return (
      <BenefitLayout
        hideFooter
        title="Hi there!"
        mainClassName="px-4 py-6 pt-0 grid gap-4 justify-center md:justify-start"
        style={{
          gridTemplateColumns: "repeat(auto-fill, 342px)",
          gridTemplateRows: "repeat(auto-fill, 316px)"
        }}
      >
        <PostPageView />
        <div className="flex flex-col items-center">
          <h2 className="text-lg font-medium text-content-1000">
            Hmm... Looks like you don't have any products or benefits enabled.
          </h2>
        </div>
      </BenefitLayout>
    );

  if (
    (!enrollment || !enrollment.submitted) &&
    partner?.renter_benefits?.includes(RenterBenefit.RENT_REPORTING)
  ) {
    navigate(`/${RenterBenefit.RENT_REPORTING}/onboarding/intro`);

    return null;
  }

  if (isLoading) return null;

  // For partners who don't need hub
  if (isNoHub)
    return (
      <Redirect
        href="/rent_reporting/current"
        options={{
          replace: true
        }}
      />
    );

  return (
    <BenefitLayout
      hideFooter
      title={`Hi, ${customer?.first_name || "..."}`}
      mainClassName="px-4 py-6 pt-0 grid gap-4 justify-center md:justify-start benefits-grid !h-auto md:h-full md:flex-1 !flex-none"
    >
      {enrollment ? (
        <PostPageView
          message={
            "Enrollment: " + enrollment.reporting_enrollment_status ||
            "onboarding in progress"
          }
        />
      ) : null}
      <Modal
        opened={Boolean(fromRentReportingSuccess)}
        onClose={() => navigate("/hub")}
        panelClassName="w-[300px] rounded-md"
      >
        <div className="p-5" style={theme}>
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-success-50 rounded-full mb-6">
            <SuccessIcon className="h-6 w-6 text-success-500" />
          </div>

          <h2 className="mb-1 text-xl font-bold text-center">Thank you!</h2>

          <p className="mb-6 text-content-400 text-sm text-center">
            Your account is being verified. We&apos;ll let you know when there
            are any updates.
          </p>

          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-full"
            onClick={() => {
              navigate("/hub");
            }}
          >
            Ok
          </Button>
        </div>
      </Modal>
      <PostPageView />
      {issuesMeta.data?.length
        ? issuesMeta.data.map((issue, idx) => (
            <IssueCard key={issue.slug + idx} {...issue} />
          ))
        : null}
      {enrollment?.submitted ? (
        <RentReportingCard
          isPending={enrollment.reporting_enrollment_status === "pending"}
          reason={getPendingReason(getPendingEnrollmentReason(enrollment))}
          enrollmentId={enrollment.id}
          rentAmount={enrollment.monthly_rent_amount.cents}
          address={`${enrollment.address?.address1}, ${enrollment.address?.city}, ${enrollment.address?.state_or_province} ${enrollment.address?.postal_code}`}
        />
      ) : null}
      {pendingBenefits.map((benefit) => (
        <BenefitCard key={benefit} benefit={benefit} />
      ))}
    </BenefitLayout>
  );
};
